import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MatchRing } from "@/components/MatchRing";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Sparkles, MapPin, Briefcase, DollarSign } from "lucide-react";

const JOB_TYPE_MAP = {
  "Full-time": "FULL_TIME",
  "Part-time": "PART_TIME",
  "Contract": "CONTRACTOR",
  "Internship": "INTERN",
  "Temporary": "TEMPORARY",
};

const useJobPostingSchema = (job) => {
  useEffect(() => {
    if (!job) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: job.description,
      datePosted: job.created_at,
      employmentType: JOB_TYPE_MAP[job.employment_type] || "FULL_TIME",
      hiringOrganization: {
        "@type": "Organization",
        name: job.company,
        sameAs: "https://axiony.co.in",
      },
      jobLocation: {
        "@type": "Place",
        address: { "@type": "PostalAddress", addressLocality: job.location || "Remote" },
      },
      ...(job.location?.toLowerCase().includes("remote") && {
        jobLocationType: "TELECOMMUTE",
        applicantLocationRequirements: { "@type": "Country", name: "Anywhere" },
      }),
      ...(job.salary_range && { baseSalary: { "@type": "MonetaryAmount", value: job.salary_range } }),
      skills: (job.required_skills || []).join(", "),
      ...(job.min_experience_years && {
        experienceRequirements: {
          "@type": "OccupationalExperienceRequirements",
          monthsOfExperience: job.min_experience_years * 12,
        },
      }),
      directApply: true,
      url: `https://axiony.co.in/jobs/${job.job_id}`,
    };

    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://axiony.co.in/" },
        { "@type": "ListItem", position: 2, name: "Jobs", item: "https://axiony.co.in/jobs" },
        { "@type": "ListItem", position: 3, name: job.title, item: `https://axiony.co.in/jobs/${job.job_id}` },
      ],
    };

    const scripts = [schema, breadcrumbs].map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-jobposting", "true");
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [job]);
};

export const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [match, setMatch] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [applying, setApplying] = useState(false);
  const streamRef = useRef(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => () => streamRef.current?.abort?.(), []);
  useJobPostingSchema(job);

  const runMatch = async () => {
    setStreaming(true);
    setMatch(null);
    setStreamText("");
    const ctrl = new AbortController();
    streamRef.current = ctrl;

    try {
      const token = localStorage.getItem("axiony_token");
      const res = await fetch(`${API}/match/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ job_id: id }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Scoring failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const msg = JSON.parse(payload);
            if (msg.type === "delta") setStreamText((t) => t + msg.text);
            else if (msg.type === "result") setMatch(msg.match);
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(err.message || "Scoring failed");
      }
    } finally {
      setStreaming(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/applications", { job_id: id });
      setMatch(data.match);
      toast.success("Application submitted");
      navigate("/candidate/applications");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  if (!job) return <div className="max-w-6xl mx-auto px-6 py-10 text-slate-400">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
      <SEO
        title={`${job.title} at ${job.company}`}
        description={`${job.title} — ${job.company} · ${job.location}. ${(job.description || "").slice(0, 140)}`}
        path={`/jobs/${job.job_id}`}
        image={`https://axiony.co.in/api/og/job/${job.job_id}.png`}
      />
      <div className="lg:col-span-2 space-y-6">
        <div>
          <div className="mono-label">{job.employment_type}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">{job.title}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
            <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.company}</span>
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
            {job.salary_range && <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />{job.salary_range}</span>}
          </div>
        </div>

        <div className="border border-white/10 bg-[#111827] rounded-xl p-6 whitespace-pre-line text-slate-300 leading-relaxed">
          {job.description}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-white/10 bg-[#111827] rounded-xl p-5">
            <div className="mono-label mb-3">required</div>
            <div className="flex flex-wrap gap-2">
              {job.required_skills.map((s) => (
                <span key={s} className="text-xs px-2 py-1 rounded border border-white/15 text-slate-200">{s}</span>
              ))}
            </div>
          </div>
          {job.nice_to_have?.length > 0 && (
            <div className="border border-white/10 bg-[#111827] rounded-xl p-5">
              <div className="mono-label mb-3">nice to have</div>
              <div className="flex flex-wrap gap-2">
                {job.nice_to_have.map((s) => (
                  <span key={s} className="text-xs px-2 py-1 rounded border border-white/10 text-slate-400">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <aside className="lg:sticky lg:top-24 h-fit">
        <div className="border border-white/10 bg-[#111827] rounded-xl p-6">
          <div className="mono-label text-[#06B6D4]">ai match score</div>
          <div className="mt-4 flex items-center gap-4">
            <MatchRing value={match?.overall ?? 0} size={96} stroke={8} testId="jobdetail-match-ring" />
            <div>
              <div className="font-display text-2xl font-semibold">
                {match ? `${match.overall}/100` : streaming ? "…" : "—"}
              </div>
              <div className="text-xs text-slate-400 mt-1">Powered by Gemini 3 Flash</div>
            </div>
          </div>

          {!match && !streaming && (
            <Button onClick={runMatch} data-testid="jobdetail-score-btn"
                    className="w-full mt-6 bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full py-6 font-medium">
              <Sparkles className="w-4 h-4 mr-2" />
              See my match
            </Button>
          )}

          {streaming && !match && (
            <div className="mt-6">
              <div className="mono-label mb-2 text-[#06B6D4] flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full pulse-ring" />
                analyzing…
              </div>
              <pre className="text-xs text-slate-400 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto bg-[#0B0F19] rounded-lg p-3 border border-white/5">
                {streamText || "…"}
              </pre>
            </div>
          )}

          {match && (
            <div className="mt-6 space-y-3">
              <ScoreRow label="Skills" v={match.skills_match} />
              <ScoreRow label="Experience" v={match.experience_match} />
              <ScoreRow label="Culture fit" v={match.culture_fit} />

              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="mono-label mb-2">summary</div>
                <p className="text-sm text-slate-300 leading-relaxed">{match.summary}</p>
              </div>

              {match.strengths?.length > 0 && (
                <div>
                  <div className="mono-label mb-2 text-[#10B981]">strengths</div>
                  <ul className="space-y-1.5">
                    {match.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-slate-300 flex gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {match.gaps?.length > 0 && (
                <div>
                  <div className="mono-label mb-2 text-[#F59E0B]">gaps</div>
                  <ul className="space-y-1.5">
                    {match.gaps.map((s, i) => (
                      <li key={i} className="text-sm text-slate-300 flex gap-2">
                        <AlertCircle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <Button onClick={apply} disabled={applying} data-testid="jobdetail-apply-btn"
                  className="w-full mt-6 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full py-6">
            {applying ? "Submitting…" : "Apply now"}
          </Button>
        </div>
      </aside>
    </div>
  );
};

const ScoreRow = ({ label, v }) => (
  <div>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-200 font-medium">{v}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full"
           style={{ width: `${v}%`, background: v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444" }} />
    </div>
  </div>
);
